import React from 'react';
import { Check, Rocket, Sparkles, Ticket, Mail, Lightbulb } from 'lucide-react';

import { cn } from 'src/utils/cn';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogTitle,
  DialogHeader,
  DialogFooter,
  DialogContent,
  DialogDescription,
} from 'src/components/ui/dialog';

export interface ProUpgradeDialogProps {
  open: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  freeTrialCount: number;
}

export interface FreeTrialConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onUpgrade: () => void;
  freeTrialCount?: number;
  featureCost?: number;
}

export const ProUpgradeDialog: React.FC<ProUpgradeDialogProps> = ({
  open,
  onClose,
  onUpgrade,
  freeTrialCount,
}) => (
  <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
    <DialogContent
      className={cn(
        'max-w-[440px] rounded-2xl p-0 overflow-hidden gap-0',
        'border-border bg-background'
      )}
    >
      <div className="relative px-6 pt-7 pb-5 bg-gradient-to-b from-primary/10 via-primary/5 to-transparent">
        <div className="pointer-events-none absolute -top-16 -right-16 h-44 w-44 rounded-full bg-primary/15 blur-3xl" />
        <div className="pointer-events-none absolute -top-10 -left-12 h-36 w-36 rounded-full bg-primary/10 blur-3xl" />

        <DialogHeader className="relative text-center space-y-3">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/25 ring-1 ring-primary/20">
            {freeTrialCount > 0 ? (
              <Sparkles className="h-7 w-7" />
            ) : (
              <Rocket className="h-7 w-7" />
            )}
          </div>
          <DialogTitle className="text-xl font-semibold tracking-tight text-foreground">
            {freeTrialCount > 0 ? 'Level Up Your Journey' : 'Ready to Unlock Pro?'}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
            {freeTrialCount > 0
              ? 'This feature is Pro-only. Upgrade to access it and every other premium tool.'
              : "You've used all your free trials. Subscribe for unlimited access to every premium feature."}
          </DialogDescription>
        </DialogHeader>
      </div>

      <div className="px-6 pb-2">
        {freeTrialCount > 0 ? (
          <ul className="flex flex-col gap-2 rounded-xl border border-border bg-muted/40 p-3">
            {['Premium AI features', 'Advanced career tools', 'Unlimited usage'].map((text) => (
              <li key={text} className="flex items-center gap-3 text-sm">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                </span>
                <span className="text-foreground">{text}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 p-3.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Mail className="h-4 w-4" />
            </span>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Need more trial points? Email{' '}
              <span className="font-medium text-foreground">jobjourney.au@gmail.com</span> with
              your name and we&apos;ll top you up.
            </p>
          </div>
        )}
      </div>

      <DialogFooter className="flex-row justify-end gap-2 px-6 py-4">
        <Button variant="ghost" onClick={onClose} className="rounded-lg">
          Maybe later
        </Button>
        <Button
          onClick={onUpgrade}
          className="rounded-lg font-medium shadow-sm transition-transform duration-150 hover:-translate-y-0.5"
        >
          Upgrade now
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const FreeTrialConfirmDialog: React.FC<FreeTrialConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  onUpgrade,
  freeTrialCount = 0,
  featureCost = 1,
}) => (
  <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
    <DialogContent
      className={cn(
        'max-w-[420px] rounded-2xl p-0 overflow-hidden gap-0',
        'border-border bg-background'
      )}
    >
      <div className="relative px-6 pt-7 pb-5 bg-gradient-to-b from-primary/10 via-primary/5 to-transparent">
        <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-primary/15 blur-3xl" />

        <DialogHeader className="relative text-center space-y-3">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/25 ring-1 ring-primary/20">
            <Ticket className="h-7 w-7" />
          </div>
          <DialogTitle className="text-xl font-semibold tracking-tight text-foreground">
            Use a Free Trial Point?
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
            You have{' '}
            <span className="font-semibold text-foreground">{freeTrialCount}</span> trial
            {freeTrialCount === 1 ? '' : 's'} left. This action uses{' '}
            <span className="font-semibold text-foreground">{featureCost}</span> point
            {featureCost === 1 ? '' : 's'}.
          </DialogDescription>
        </DialogHeader>
      </div>

      <div className="px-6 pb-2">
        <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 p-3.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Lightbulb className="h-4 w-4" />
          </span>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Subscribe for unlimited access to every premium feature — no trial points needed.
          </p>
        </div>
      </div>

      <DialogFooter className="flex-row justify-end gap-2 px-6 py-4">
        <Button variant="ghost" onClick={onClose} className="rounded-lg">
          Cancel
        </Button>
        <Button variant="outline" onClick={onUpgrade} className="rounded-lg">
          Subscribe
        </Button>
        <Button
          onClick={onConfirm}
          className="rounded-lg font-medium shadow-sm transition-transform duration-150 hover:-translate-y-0.5"
        >
          Use point
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
